import {THEME} from "./theme";
import {AppConfig, BaseAppProps, BaseAppRefs, BaseAppState} from "shared/BaseApp";
import React, {createRef, ReactElement, RefObject} from 'react';
import {Main} from "./Main";
import {PathComponent} from "../index";
import {FIREBASE_CONFIG} from "../consts";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {
  Account,
  AccountProfile,
  AppPrefListener,
  ProvisionedAppConfig,
  UserDisplayName,
  UserProfilePhoto
} from "../shared/types";
import {AppPrefs} from "./app_prefs";
import {BaseAppPrefs} from "../shared/BaseAppPrefs";
import {LoginConfig} from "../shared/Login";
import defaultUserImage from "./res/images/default_user.png";
import {User as FirebaseUser} from "@firebase/auth";
import {Settings} from "./settings/settings";
import {
  AccountProfileContextTrait,
  AccountTypeName,
  loadProfileForTypeId,
  PageClaims,
  Pages,
  PageTypeName,
  PracticeData
} from "../libwyze-shared/types";
import {getMemberAuth} from "../shared/auth";
import {LibwyzeCommonInit} from "../libwyze-shared/init";
import * as Sentry from "@sentry/react";
import {Member, Members, User} from "../shared/entities";
import {
  ChatParticipantMetadata,
  ChatThreadCustomParticipantTypeLoader,
  ChatThreads,
  ParticipantKey,
  ParticipantType
} from "@/app-modules/messages/chat/types";
import {MessagesContextTrait} from "@/app-modules/messages/types";
import {BORDER_RADIUS} from "../shared/dimens";
import {NotificationsContextTrait, NotificationsSource} from "@/libwyze-shared/types";
import {AppNotificationsSource} from "./notifications/AppNotificationsSource";
import {VantagePracticeData} from "../vantage-shared/types";

Sentry.init({
  dsn: "https://8a74d4e0e56bc73c7af7f048cb17bdfb@o4511030386229248.ingest.us.sentry.io/4511030423781376",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

export class VantageAppRefs {

  constructor(
    readonly localAudioRenderer: RefObject<HTMLAudioElement>,
    readonly localAudioElement: ReactElement,
    readonly localVideoRenderer: RefObject<HTMLVideoElement>,
    readonly localVideoElement: ReactElement,
    readonly remoteAudioRenderer: RefObject<HTMLAudioElement>,
    readonly remoteAudioElement: ReactElement,
    readonly remoteVideoRenderer: RefObject<HTMLVideoElement>,
    readonly remoteVideoElement: ReactElement) {
  }
}

export class App extends ProvisionedApp<AppProps, AppState> implements AccountProfileContextTrait, AppPrefListener, MessagesContextTrait, NotificationsContextTrait {

  private static appRefs: VantageAppRefs;

  static getAppRefs(): VantageAppRefs {
    if (!App.appRefs) {
      App.appRefs = App.createAppRefs();
    }
    return App.appRefs;
  }

  private static createAudioElement(ref: RefObject<HTMLAudioElement>, muted: boolean): ReactElement {
    return <audio ref={ref} muted={muted}/>;
  }

  private static createVideoElement(ref: RefObject<HTMLVideoElement>): ReactElement {
    return <video
      ref={ref} autoPlay playsInline muted
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: BORDER_RADIUS,
      }}></video>;
  }

  private static createAppRefs() {
    const localAudioRenderer = createRef<HTMLAudioElement>();
    const localVideoRenderer = createRef<HTMLVideoElement>();
    const remoteAudioRenderer = createRef<HTMLAudioElement>();
    const remoteVideoRenderer = createRef<HTMLVideoElement>();
    return new VantageAppRefs(
      localAudioRenderer,
      App.createAudioElement(localAudioRenderer, true),
      localVideoRenderer,
      App.createVideoElement(localVideoRenderer),
      remoteAudioRenderer,
      App.createAudioElement(remoteAudioRenderer, false),
      remoteVideoRenderer,
      App.createVideoElement(remoteVideoRenderer));
  }

  getAppRefs<T extends BaseAppRefs>(): T {
    // @ts-ignore
    return App.getAppRefs() as T;
  }

  protected getLoginConfig(): LoginConfig | null | undefined {
    return {
      provisioning: {
        membersUseSharedProvisioningContext: true,
      },
      uiProps: {
        bottomDecorator: <img src={"/images/dentwyze_vantage_logotype.svg"} style={{height: 48}}/>,
      },
    };
  }

  private notificationsSource;
  private account: Account;
  private accountProfile: AccountProfile;

  private readonly myParticipantKeys = new Map<ParticipantType, ParticipantKey>();

  getMyParticipantKey(type: ParticipantType = ParticipantType.CUSTOM): ParticipantKey {
    return this.myParticipantKeys.get(type);
  }

  getNotificationsSource(): NotificationsSource {
    return this.notificationsSource;
  }

  private async initAskWyze(): Promise<void> {
    const askWyze = new Member("askwyze", "askwyze");
    askWyze.user = new User("askwyze", "Wyze", "", "/images/askwyze_icon.png");
    Members.getInstance().setBotMembers([
      askWyze,
    ]);
  }

  protected async onAppInitWithLoggedInUser(user: FirebaseUser): Promise<void> {
    await super.onAppInitWithLoggedInUser(user);
    await Settings.loadInstance();
    await LibwyzeCommonInit.init(user);
    await this.initAskWyze();

    const memberAuth = getMemberAuth();
    this.account = await memberAuth.getOrLoadAccountByType(AccountTypeName.PROVIDER);
    this.accountProfile = await this.account?.getOrLoadProfile();
    if (this.accountProfile?.type === AccountTypeName.PROVIDER) {
      const claims = await PageClaims.getInstance().loadPageClaimsByAccountId(this.account.id);
      for (const claim of claims) {
        await Pages.getInstance().getOrLoadItem(claim.id);
      }
      const pageId = this.getProviderPageId();
      if (pageId) {
        await Pages.getInstance().getOrLoadItem(pageId);
      }
      const practicePageId = this.getPracticePageId();
      if (practicePageId) {
        await Pages.getInstance().getOrLoadItem(practicePageId);
        await PracticeData.createAndInitProvider(practicePageId);
        await VantagePracticeData.createAndInitProvider(practicePageId);

        // TODO: This should be done for staff as well.
        this.myParticipantKeys.set(ParticipantType.CUSTOM, new ParticipantKey(PracticeData.provider().myProfile().toTypeId().replace("/", "_"), ParticipantType.CUSTOM, `practiceData/${practicePageId}`));
        ChatThreads.setCustomParticipantTypeLoader(new class implements ChatThreadCustomParticipantTypeLoader {
          async loadParticipantMetadata(participantId: string): Promise<ChatParticipantMetadata | null> {
            const botMember = Members.getInstance().getBotMember(participantId);
            if (botMember) {
              return {
                title: UserDisplayName(botMember.user),
                text: botMember.user.email,
                profilePhoto: UserProfilePhoto(botMember.user)
              };
            }
            const profile = await loadProfileForTypeId(participantId);
            await profile.onAfterItemDeserialized();
            return {
              title: profile.displayName(),
              text: profile.basicInfo.email,
              profilePhoto: profile.basicInfo.profilePhoto,
            };
          }
        });
      }

      // Notifications
      // This should be done after account setup so that PracticeData.notifications is initialized.
      // TODO: For staff
      const practiceData = PracticeData.shared();
      // This may be null if no clinic is linked to account yet.
      if (practiceData) {
        this.notificationsSource = new AppNotificationsSource(practiceData.notifications, "/notifications");
        await this.notificationsSource.init();
      }
    }
  }

  getAccountProfile<T extends AccountProfile>(): T {
    return this.accountProfile as T;
  }

  getPracticePageId(): string | undefined {
    const selectedPracticeId = AppPrefs.getInstance().getSelectedPracticeId();
    const claims = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
      .filter(claim => claim.pageType === PageTypeName.PRACTICE);
    let pageId = claims.find(claim => claim.id === selectedPracticeId)?.id;
    if (!pageId && claims.length > 0) {
      pageId = claims[0].pageId;
      AppPrefs.getInstance().setSelectedPracticeId(pageId);
    }
    return pageId;
  }

  getProviderPageId(): string | undefined {
    switch (this.accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const claim = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
          .find(claim => claim.pageType === PageTypeName.PROVIDER);
        return claim?.pageId;
      }
    }
    return undefined;
  }

  getPatientLinkedPracticePageIds(): string[] {
    const accountProfile = this.getAccountProfile();
    switch (accountProfile.type) {
      case AccountTypeName.PROVIDER: {
        const claims = PageClaims.getInstance().getPageClaimsByAccountId(this.account.id)
          .filter(claim => claim.pageType === PageTypeName.PRACTICE);
        return claims.map(claim => claim.pageId);
      }
    }
    return [];
  }

  getProviderClaimedPracticePageIds(): string[] {
    return [];
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: "Vantage",
      icon: "/logo192.png",
      logo: "/logo192.png",
      stamp: "/images/dentwyze_logotype.svg",
      stampHeight: 40,
      defaultUserImage: defaultUserImage,
      theme: THEME,
      privacyUrl: "",
      termsUrl: "",
      styleConfig: {
        defaultContainerSize: "xxxlg",
      },
      loginConfig: this.getLoginConfig(),
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }

  getProvisionedAppId(): string {
    return "dentwyze";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig | null {
    return undefined;
  }

  static nestedPaths(): PathComponent[] {
    return Main.nestedPaths();
  }

  getAppPrefs<T extends BaseAppPrefs>(): T {
    //@ts-ignore
    return AppPrefs.getInstance() as T;
  }

  protected renderMain(): ReactElement {
    return <Main path={this.props.path}/>;
  }
}
